// apps/client/src/ui/screens/shop.tsx (M1-T14) — Bramble's counter: Buy (today's picks, then what is always in stock) and Sell tabs.
// Shop hours come from tuning and the synced town clock; every hour shown in text goes through hourText (contracts rev 6).
import { getEntry, t as tt, tuning, type CatalogId, type StringKey } from '@whiskerton/content';
import { townTime } from '@whiskerton/shared';
import { isShopOpen } from '@whiskerton/shared/rules/shop';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { getGame, useCommands } from '../../game/instance';
import { buyItem } from '../../state/slices/shop';
import { useGame } from '../../state/store';
import { itemName, useT } from '../i18n';
import { useIcon } from '../items/useIcon';
import { Bubble, Button, Panel, Tabs, Tag } from '../kit';
import type { ScreenDef } from '../router';
import { injectShopStyle, SellGrid } from './sell';

declare module '../router' { interface ScreenParams { shop: undefined } }

/** 8 → "8 AM", 22 → "10 PM", via the `time` strings (contracts rev 6); hours themselves always come from tuning. */
export const hourText = (h: number): string => tt(h < 12 ? 'time.hourAm' : 'time.hourPm', { h: h % 12 || 12 });
/** Bramble greets with `shop.helloEvening` from this hour (presentation only). */
const EVENING_HOUR = 17;
/** The town's wall clock now: the socket's server-synced clock, or the device clock before there is one. */
export function townNow(timeZone: string) { return townTime(getGame().socket?.clock.now() ?? Date.now(), timeZone); }

function Row({ id, price, bought, bells, open, busy, onBuy }: { id: CatalogId; price: number; bought: boolean; bells: number; open: boolean; busy: boolean; onBuy(): void }) {
  const t = useT();
  const icon = useIcon(id);
  const e = getEntry(id);
  return (
    <div className="shop-row">
      <img src={icon} alt="" />
      <span className="shop-name">{e && e.kind !== 'world' ? itemName(e.name) : id}</span>
      <span className="shop-price">{t('shop.price', { price })}</span>
      {bought ? <Tag color="villager">{t('shop.boughtToday')}</Tag>
        : <Button disabled={!open || busy || bells < price} onClick={onBuy}>{t('shop.buyButton', { price })}</Button>}
    </div>
  );
}

function Shop({ close }: { close: () => void }) {
  injectShopStyle();
  const t = useT();
  const cmd = useCommands();
  const { you, town, space, tab, setTab } = useGame(useShallow((s) => ({ you: s.you, town: s.town, space: s.space, tab: s.shopTab, setTab: s.shopSetTab,
    minute: s.clock.minuteOfDay })));                                    // re-render every town minute, so opening and closing show
  const [line, setLine] = useState<StringKey | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (space !== 'shop') close(); }, [space, close]);  // left the shop (or the town) under the panel
  if (!you?.shop || !town) return null;
  const now = townNow(town.timeZone), open = isShopOpen(now);
  const buy = (id: CatalogId) => {
    setBusy(true);
    void buyItem(cmd.act, id).then((r) => {
      setBusy(false);
      if (!r.ok) return;
      getGame().sound?.sfx('coin');
      setLine('shop.sold');
    });
  };
  const row = (d: { catalogId: CatalogId; price: number }, bought: boolean) => (
    <Row key={d.catalogId} id={d.catalogId} price={d.price} bought={bought} bells={you.bells} open={open} busy={busy} onBuy={() => buy(d.catalogId)} />
  );
  return (
    <Panel title={t('shop.title')} className="shop-panel">
      <div className="shop-head">
        <Bubble name={t('shop.shopkeeperName')}>{t(line ?? (now.hour < EVENING_HOUR ? 'shop.hello' : 'shop.helloEvening'))}</Bubble>
        <Tabs tabs={[{ id: 'buy', label: t('shop.buy') }, { id: 'sell', label: t('shop.sell') }]} active={tab} onChange={(id) => setTab(id === 'sell' ? 'sell' : 'buy')} />
      </div>
      {!open && <p className="shop-note">{t('shop.closedNote', { open: hourText(tuning.shop.openHour), close: hourText(tuning.shop.closeHour) })}</p>}
      {tab === 'buy' ? (
        <div className="shop-list">
          <h4>{t('shop.today')}</h4>
          {you.shop.daily.map((d) => row(d, d.bought))}
          <h4>{t('shop.always')}</h4>
          {you.shop.always.map((d) => row(d, false))}
        </div>
      ) : <SellGrid where="shop" open={open} onSold={() => setLine('shop.sold')} />}
    </Panel>
  );
}
export const screen: ScreenDef<'shop'> = { id: 'shop', layer: 'panel', component: ({ close }) => <Shop close={close} /> };
